import React, { useCallback, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { useParams, useHistory } from 'react-router-dom'
import {
  Tabs,
  Descriptions,
  Table,
  Spin,
  Tag,
  Space,
  Button,
  DatePicker,
  message,
  Modal,
  Select,
  Input,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
} from '@ant-design/icons'
import ptBR from 'antd/es/date-picker/locale/pt_BR'
import { createSimpleStore } from 'react-simple-reducer'
import { useFormik } from 'formik'
import dayjs from 'dayjs'
import { TurmaModel } from '../../models/TurmaModel'
import { MatriculaModel } from '../../models/MatriculaModel'
import api from '../../api'
import { CopyButton } from '../../components/CopyButton'
import { Show } from '../../components/Util'
import { ImportarCsvTurma } from './ImportarCsvTurma'
import { TextFieldInput } from '../../components/TextField'
import { notificarSolicitacoesDeTurmaAtualizadas } from '../../lib/turmasEvents'

const { TabPane } = Tabs

const PARCEIRO_ID_NEWDRIVER = 158

const TurmaDetailStore = createSimpleStore(
  {
    turma: null as TurmaModel | null,
    matriculas: [] as MatriculaModel[],
    loading: true,
    editingVencimento: false,
    savingVencimento: false,
    gerandoPix: false,
    gerandoBoleto: false,
  },
  {
    setTurma: (state, turma) => ({
      ...state,
      turma,
      loading: false,
    }),
    setMatriculas: (state, matriculas) => ({
      ...state,
      matriculas,
    }),
    setLoading: (state, loading) => ({
      ...state,
      loading,
    }),
    setEditingVencimento: (state, editingVencimento: boolean) => ({
      ...state,
      editingVencimento,
    }),
    setSavingVencimento: (state, savingVencimento: boolean) => ({
      ...state,
      savingVencimento,
    }),
    applyVencimentoUpdate: (state, vencimento: string) => ({
      ...state,
      turma: state.turma ? { ...state.turma, vencimento } : null,
      editingVencimento: false,
      savingVencimento: false,
    }),
    setGerandoPix: (state, gerandoPix: boolean) => ({ ...state, gerandoPix }),
    setGerandoBoleto: (state, gerandoBoleto: boolean) => ({ ...state, gerandoBoleto }),
  },
  {
    thunks: {
      fetchTurma(id: number) {
        return async (dispatch) => {
          try {
            dispatch(TurmaDetailStore.actions.setLoading(true))
            const turma = await api.Turmas.getById(id)
            dispatch(TurmaDetailStore.actions.setTurma(turma))

            if (turma.matriculas) {
              dispatch(TurmaDetailStore.actions.setMatriculas(turma.matriculas))
            }
          } catch (error) {
            console.error('Error fetching turma:', error)
            dispatch(TurmaDetailStore.actions.setLoading(false))
          }
        }
      },
      saveVencimento(turmaId: number, novoVencimento: string) {
        return async (dispatch) => {
          dispatch(TurmaDetailStore.actions.setSavingVencimento(true))
          try {
            await api.Turmas.updateVencimento(turmaId, novoVencimento)
            dispatch(TurmaDetailStore.actions.applyVencimentoUpdate(novoVencimento))
            message.success('Vencimento atualizado com sucesso')
          } catch (error) {
            console.error('Error saving vencimento:', error)
            message.error('Erro ao salvar vencimento')
            dispatch(TurmaDetailStore.actions.setSavingVencimento(false))
          }
        }
      },
      gerarCobranca(turmaId: number, tipo: 'PIX' | 'BOLETO') {
        return async (dispatch) => {
          const setGerando =
            tipo === 'PIX'
              ? TurmaDetailStore.actions.setGerandoPix
              : TurmaDetailStore.actions.setGerandoBoleto
          dispatch(setGerando(true))
          try {
            await api.Turmas.gerarCobranca(turmaId, tipo)
            const turma = await api.Turmas.getById(turmaId)
            dispatch(TurmaDetailStore.actions.setTurma(turma))
          } catch (e: any) {
            message.error(e?.message ?? `Erro ao gerar ${tipo}`)
          } finally {
            dispatch(setGerando(false))
          }
        }
      },
    },
  }
)

export const TurmaDetail = () => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()

  const init = useCallback(
    (dispatch) => {
      dispatch(TurmaDetailStore.thunks.fetchTurma(parseInt(id)))
    },
    [id]
  )

  return (
    <TurmaDetailStore.Provider init={init}>
      <Layout>
        <Container>
          <Header>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => history.push('/turmas')}
              style={{ marginRight: 16 }}
            >
              Voltar
            </Button>
            <Title>Detalhes da Turma</Title>
          </Header>
          <TurmaContent />
        </Container>
      </Layout>
    </TurmaDetailStore.Provider>
  )
}

function TurmaContent() {
  const { turma, loading } = TurmaDetailStore.useState()

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    )
  }

  if (!turma) {
    return <div>Turma não encontrada</div>
  }

  return (
    <ContentWrapper>
      {turma.statusTurma === 'SOLICITADA' && <SolicitacaoSegment />}
      <Tabs defaultActiveKey="1">
        <TabPane tab="Informações da Turma" key="1">
          <TurmaInfoSegment turma={turma} />
        </TabPane>
        <TabPane tab="Informações de Cobrança" key="2">
          <ChargeInfoSegment turma={turma} />
        </TabPane>
        <TabPane tab="Nota Fiscal" key="3">
          <InvoiceInfoSegment turma={turma} />
        </TabPane>
        <TabPane tab="Alunos da Turma" key="4">
          <MembersSegment />
        </TabPane>
      </Tabs>
    </ContentWrapper>
  )
}

function SolicitacaoSegment() {
  const { turma } = TurmaDetailStore.useState()
  const dispatch = TurmaDetailStore.useDispatch()
  const [concluindo, setConcluindo] = useState(false)
  if (!turma) return null

  async function concluir() {
    setConcluindo(true)
    try {
      await api.Turmas.concluirSolicitacao(turma!.id)
      dispatch(TurmaDetailStore.thunks.fetchTurma(turma!.id))
      notificarSolicitacoesDeTurmaAtualizadas()
      message.success('Solicitação concluída')
    } catch {
      message.error('Erro ao concluir solicitação')
    } finally {
      setConcluindo(false)
    }
  }

  return (
    <SegmentContainer style={{ borderColor: '#faad14', background: '#fffbe6' }}>
      <SubTitle>Solicitação do parceiro</SubTitle>
      <p>{turma.obs || 'Sem observações.'}</p>
      <Space>
        {turma.arquivoAlunosUrl && (
          <Button
            icon={<DownloadOutlined />}
            href={turma.arquivoAlunosUrl}
            target="_blank"
            rel="noreferrer"
          >
            Baixar lista de alunos
          </Button>
        )}
        {!turma.arquivoAlunosUrl && <span>Nenhuma lista de alunos enviada.</span>}
        <Button type="primary" icon={<CheckOutlined />} loading={concluindo} onClick={concluir}>
          Concluir solicitação
        </Button>
      </Space>
    </SegmentContainer>
  )
}

function TurmaInfoSegment({ turma }: { turma: TurmaModel }) {
  return (
    <SegmentContainer>
      <Descriptions bordered column={2}>
        <Descriptions.Item label="ID">{turma.id}</Descriptions.Item>
        <Descriptions.Item label="UF">{turma.uf}</Descriptions.Item>
        <Descriptions.Item label="Descrição" span={2}>
          {turma.descricaoTurma}
        </Descriptions.Item>
        <Descriptions.Item label="Valor New Driver">
          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
            turma.pagamento?.valorNewDriver || 0
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Valor Cobrança">
          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
            turma.pagamento?.valor || 0
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Parceiro">{turma.parceiro?.nome || '-'}</Descriptions.Item>
        <Descriptions.Item label="Permite Emissão de Certificado" span={2}>
          <Tag color={turma.permiteEmissaoCertificado ? 'green' : 'red'}>
            {turma.permiteEmissaoCertificado ? 'Sim' : 'Não'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Criado em">
          {turma.createdAt ? new Date(turma.createdAt).toLocaleString('pt-BR') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Atualizado em">
          {turma.updatedAt ? new Date(turma.updatedAt).toLocaleString('pt-BR') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Obs" span={2}>
          <ObsField turmaId={turma.id} initialValue={turma.obs} />
        </Descriptions.Item>
      </Descriptions>
    </SegmentContainer>
  )
}

function ObsField({ turmaId, initialValue }: { turmaId: number; initialValue?: string }) {
  const dispatch = TurmaDetailStore.useDispatch()
  const [value, setValue] = useState(initialValue ?? '')
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(false)

  const handleSalvar = async () => {
    setSaving(true)
    try {
      await api.Turmas.createOrUpdate({ id: turmaId, obs: value })
      const turma = await api.Turmas.getById(turmaId)
      dispatch(TurmaDetailStore.actions.setTurma(turma))
      setEditing(false)
      message.success('Obs salvo')
    } catch {
      message.error('Erro ao salvar Obs')
    } finally {
      setSaving(false)
    }
  }

  const handleCancelar = () => {
    setValue(initialValue ?? '')
    setEditing(false)
  }

  return (
    <div>
      <Input.TextArea
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          setEditing(true)
        }}
        rows={3}
        style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}
      />
      <Show condition={editing}>
        <Space style={{ marginTop: 8 }}>
          <Button type="primary" size="small" onClick={handleSalvar} loading={saving}>
            Salvar
          </Button>
          <Button size="small" onClick={handleCancelar} disabled={saving}>
            Cancelar
          </Button>
        </Space>
      </Show>
    </div>
  )
}

function ChargeInfoSegment({ turma }: { turma: TurmaModel }) {
  const { editingVencimento, savingVencimento } = TurmaDetailStore.useState()
  const dispatch = TurmaDetailStore.useDispatch()

  const getStatusTag = () => {
    if (turma.pagamento?.dataPagamento) return <Tag color="green">Pago</Tag>
    const vencimento = turma.vencimento ? new Date(turma.vencimento) : null
    if (vencimento && vencimento < new Date()) return <Tag color="red">Vencido</Tag>
    return <Tag color="blue">Aberto</Tag>
  }

  const handleSaveVencimento = (date: dayjs.Dayjs | null) => {
    if (!date) return
    dispatch(TurmaDetailStore.thunks.saveVencimento(turma.id, date.toISOString()))
  }

  return (
    <SegmentContainer>
      <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
        <Descriptions.Item label="Status">{getStatusTag()}</Descriptions.Item>
        <Descriptions.Item label="Referência de Pagamento">
          {turma.pagamento?.referenciaPagamento || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Vencimento">
          {editingVencimento ? (
            <Space>
              <DatePicker
                defaultValue={turma.vencimento ? dayjs(turma.vencimento) : undefined}
                format="DD/MM/YYYY"
                locale={ptBR}
                onChange={handleSaveVencimento}
                disabled={savingVencimento}
                autoFocus
              />
              <Button
                icon={<CloseOutlined />}
                size="small"
                onClick={() => dispatch(TurmaDetailStore.actions.setEditingVencimento(false))}
                disabled={savingVencimento}
              />
            </Space>
          ) : (
            <Space>
              {turma.vencimento ? new Date(turma.vencimento).toLocaleDateString('pt-BR') : '-'}
              <Button
                icon={<EditOutlined />}
                size="small"
                type="text"
                onClick={() => dispatch(TurmaDetailStore.actions.setEditingVencimento(true))}
              />
            </Space>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Data de Pagamento">
          {turma.pagamento?.dataPagamento
            ? new Date(turma.pagamento.dataPagamento).toLocaleDateString('pt-BR')
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Valor New Driver" span={2}>
          <ValorInlineEdit
            turmaId={turma.id}
            label="valorNewDriver"
            currentValue={turma.pagamento?.valorNewDriver}
          />
        </Descriptions.Item>
        <Descriptions.Item label="Valor Cobrança" span={2}>
          <ValorInlineEdit
            turmaId={turma.id}
            label="valorCobranca"
            currentValue={turma.pagamento?.valor}
          />
        </Descriptions.Item>
        <Descriptions.Item label="Descrição da Cobrança" span={2}>
          <DescricaoCobrancaEdit turmaId={turma.id} />
        </Descriptions.Item>
      </Descriptions>

      <SubTitle>Dados do Pagador</SubTitle>
      <DadosPagadorCard turmaId={turma.id} />

      <SubTitle>Gerar Cobrança</SubTitle>
      <GerarCobrancaSection turmaId={turma.id} />
    </SegmentContainer>
  )
}

function ValorInlineEdit({
  turmaId,
  label,
  currentValue,
}: {
  turmaId: number
  label: 'valorNewDriver' | 'valorCobranca'
  currentValue: number
}) {
  const dispatch = TurmaDetailStore.useDispatch()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [inputValue, setInputValue] = useState(String(currentValue ?? ''))

  const handleSalvar = async () => {
    setSaving(true)
    try {
      const numericValue = parseFloat(inputValue.replace(/[^\d,]/g, '').replace(',', '.'))
      await api.Turmas.atualizarValores(turmaId, { [label]: numericValue })
      const turma = await api.Turmas.getById(turmaId)
      dispatch(TurmaDetailStore.actions.setTurma(turma))
      setEditing(false)
      message.success('Valor atualizado')
    } catch (e: any) {
      message.error(e?.message ?? 'Erro ao atualizar valor')
    } finally {
      setSaving(false)
    }
  }

  if (!editing) {
    return (
      <Space>
        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
          currentValue || 0
        )}
        <Button icon={<EditOutlined />} size="small" type="text" onClick={() => setEditing(true)} />
      </Space>
    )
  }

  return (
    <Space>
      <TextFieldInput
        mask="currency"
        value={inputValue}
        onChange={(e: any) => setInputValue(e.target.value)}
        autoFocus
      />
      <Button type="primary" size="small" onClick={handleSalvar} loading={saving}>
        Salvar
      </Button>
      <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
        Cancelar
      </Button>
    </Space>
  )
}

function DescricaoCobrancaEdit({ turmaId }: { turmaId: number }) {
  const { turma } = TurmaDetailStore.useState()
  const dispatch = TurmaDetailStore.useDispatch()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [value, setValue] = useState(turma?.descricaoCobranca ?? '')

  const placeholderText = turma
    ? `Turma ${turma.descricaoTurma}; com ${turma.matriculas?.length ?? 0} alunos`
    : ''

  const handleSalvar = async () => {
    setSaving(true)
    try {
      await api.Turmas.createOrUpdate({ id: turmaId, descricaoCobranca: value })
      const updatedTurma = await api.Turmas.getById(turmaId)
      dispatch(TurmaDetailStore.actions.setTurma(updatedTurma))
      setEditing(false)
      message.success('Descrição salva')
    } catch {
      message.error('Erro ao salvar descrição')
    } finally {
      setSaving(false)
    }
  }

  if (!editing) {
    return (
      <Space>
        <span>
          {turma?.descricaoCobranca || <em style={{ color: '#aaa' }}>{placeholderText}</em>}
        </span>
        <Button icon={<EditOutlined />} size="small" type="text" onClick={() => setEditing(true)} />
      </Space>
    )
  }

  return (
    <Space>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholderText}
        style={{ width: 320 }}
        autoFocus
      />
      <Button type="primary" size="small" onClick={handleSalvar} loading={saving}>
        Salvar
      </Button>
      <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
        Cancelar
      </Button>
    </Space>
  )
}

export function DadosPagadorCard({ turmaId }: { turmaId: number }) {
  const { turma } = TurmaDetailStore.useState()
  const dispatch = TurmaDetailStore.useDispatch()
  const dadosIniciais = turma?.dadosPagador ?? turma?.pagamento?.dadosPagador

  const formik = useFormik({
    initialValues: {
      cpfCnpj: dadosIniciais?.cpfCnpj ?? '',
      nome: dadosIniciais?.nome ?? '',
      email: dadosIniciais?.email ?? '',
      telefone: dadosIniciais?.telefone ?? '',
      cep: dadosIniciais?.cep ?? '',
      logradouro: dadosIniciais?.logradouro ?? '',
      numero: dadosIniciais?.numero ?? '',
      complemento: dadosIniciais?.complemento ?? '',
      bairro: dadosIniciais?.bairro ?? '',
      municipio: dadosIniciais?.municipio ?? '',
      uf: dadosIniciais?.uf ?? '',
      codigoCidadeIbge: dadosIniciais?.codigoCidadeIbge ?? '',
    },
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await api.Turmas.updateDadosPagador(turmaId, values)
        dispatch(TurmaDetailStore.thunks.fetchTurma(turmaId))
        message.success('Dados do pagador salvos')
      } catch {
        message.error('Erro ao salvar dados do pagador')
      } finally {
        setSubmitting(false)
      }
    },
  })

  async function buscarCnpj() {
    const cnpjLimpo = formik.values.cpfCnpj.replace(/\D/g, '')
    if (cnpjLimpo.length !== 14) return
    try {
      const data = await api.NotasFiscais.buscarCnpj(cnpjLimpo)
      const cep = await api.NotasFiscais.buscarCep(data.cep)
      await formik.setValues({
        ...formik.values,
        nome: data.razao_social ?? formik.values.nome,
        cep: data.cep ?? '',
        logradouro: data.logradouro ?? '',
        numero: data.numero ?? '',
        bairro: data.bairro ?? '',
        municipio: data.municipio ?? '',
        uf: data.uf ?? '',
        codigoCidadeIbge: data.codigo_municipio_ibge ? '' + data.codigo_municipio_ibge : '',
      })
    } catch {
      message.error('CNPJ não encontrado')
    }
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, maxWidth: 600 }}>
        <div>
          <TextFieldInput
            label="CPF/CNPJ"
            mask="cpf-cnpj"
            name="cpfCnpj"
            value={formik.values.cpfCnpj}
            onChange={formik.handleChange}
          />
          {formik.values.cpfCnpj.replace(/\D/g, '').length === 14 && (
            <Button size="small" onClick={buscarCnpj} style={{ marginTop: 4 }}>
              Buscar
            </Button>
          )}
        </div>
        <TextFieldInput
          label="Nome"
          name="nome"
          value={formik.values.nome}
          onChange={formik.handleChange}
        />
        <TextFieldInput
          label="E-mail"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
        />
        <TextFieldInput
          label="Telefone"
          mask="telefone"
          name="telefone"
          value={formik.values.telefone}
          onChange={formik.handleChange}
        />
        <TextFieldInput
          label="CEP"
          mask="cep"
          name="cep"
          value={formik.values.cep}
          onChange={formik.handleChange}
        />
        <TextFieldInput
          label="Logradouro"
          name="logradouro"
          value={formik.values.logradouro}
          onChange={formik.handleChange}
        />
        <TextFieldInput
          label="Número"
          name="numero"
          value={formik.values.numero}
          onChange={formik.handleChange}
        />
        <TextFieldInput
          label="Complemento"
          name="complemento"
          value={formik.values.complemento}
          onChange={formik.handleChange}
        />
        <TextFieldInput
          label="Bairro"
          name="bairro"
          value={formik.values.bairro}
          onChange={formik.handleChange}
        />
        <TextFieldInput
          label="Município"
          name="municipio"
          value={formik.values.municipio}
          onChange={formik.handleChange}
        />
        <TextFieldInput
          label="UF"
          name="uf"
          value={formik.values.uf}
          onChange={formik.handleChange}
        />
        <TextFieldInput
          label="Código IBGE"
          name="codigoCidadeIbge"
          value={formik.values.codigoCidadeIbge}
          onChange={formik.handleChange}
        />
      </div>
      <Button
        type="primary"
        htmlType="submit"
        loading={formik.isSubmitting}
        style={{ marginTop: 16 }}
      >
        Salvar Pagador
      </Button>
    </form>
  )
}

export function GerarCobrancaSection({ turmaId }: { turmaId: number }) {
  const { turma } = TurmaDetailStore.useState()
  const isNewDriver = turma?.parceiroId === PARCEIRO_ID_NEWDRIVER

  return (
    <div>
      <Show condition={isNewDriver}>
        <PixSection turmaId={turmaId} />
      </Show>
      <BoletoSection turmaId={turmaId} />
    </div>
  )
}

function PixSection({ turmaId }: { turmaId: number }) {
  const { turma, gerandoPix } = TurmaDetailStore.useState()
  const dispatch = TurmaDetailStore.useDispatch()
  const infoPix = turma?.pagamento?.infoPix

  const gerarPix = () => dispatch(TurmaDetailStore.thunks.gerarCobranca(turmaId, 'PIX'))

  return (
    <div style={{ marginBottom: 12 }}>
      <Show condition={!infoPix?.pix}>
        <Button onClick={gerarPix} loading={gerandoPix}>
          Gerar PIX
        </Button>
      </Show>
      <Show condition={!!infoPix?.pix}>
        <Space direction="vertical">
          <Space>
            <code style={{ fontSize: 11 }}>{infoPix?.pix}</code>
            <CopyButton value={infoPix?.pix ?? ''} size="small" />
          </Space>
          <Button type="link" size="small" onClick={gerarPix} loading={gerandoPix}>
            Gerar Novo PIX
          </Button>
        </Space>
      </Show>
    </div>
  )
}

function BoletoSection({ turmaId }: { turmaId: number }) {
  const { turma, gerandoBoleto } = TurmaDetailStore.useState()
  const dispatch = TurmaDetailStore.useDispatch()
  const infoBoleto = turma?.pagamento?.infoBoleto

  const gerarBoleto = () => dispatch(TurmaDetailStore.thunks.gerarCobranca(turmaId, 'BOLETO'))

  return (
    <div>
      <Show condition={!infoBoleto?.chargeId}>
        <Space>
          <Button onClick={gerarBoleto} loading={gerandoBoleto}>
            Gerar Boleto
          </Button>
        </Space>
      </Show>
      <Show condition={!!infoBoleto?.chargeId}>
        <Space direction="vertical">
          <a href={infoBoleto?.linkPdf} target="_blank" rel="noreferrer">
            Abrir PDF do Boleto
          </a>
          <Space>
            <code style={{ fontSize: 11 }}>{infoBoleto?.linhaDigitavel}</code>
            <CopyButton value={infoBoleto?.linhaDigitavel ?? ''} size="small" />
          </Space>
          <Button type="link" size="small" onClick={gerarBoleto} loading={gerandoBoleto}>
            Gerar Novo Boleto
          </Button>
        </Space>
      </Show>
    </div>
  )
}

function InvoiceInfoSegment({ turma }: { turma: TurmaModel }) {
  return (
    <SegmentContainer>
      <Descriptions bordered column={1}>
        <Descriptions.Item label="ID da Nota Fiscal">
          {turma.notaFiscalId || 'Não emitida'}
        </Descriptions.Item>
        <Descriptions.Item label="Data de Notificação dos Alunos">
          {turma.dataNotificacaoAlunos
            ? new Date(turma.dataNotificacaoAlunos).toLocaleString('pt-BR')
            : 'Não notificados'}
        </Descriptions.Item>
      </Descriptions>

      <Show condition={turma.notaFiscalId}>
        <InfoText>
          Para visualizar a nota fiscal completa, acesse o módulo de Notas Fiscais.
        </InfoText>
      </Show>
    </SegmentContainer>
  )
}

function MembersSegment() {
  const { turma, matriculas } = TurmaDetailStore.useState()
  const dispatch = TurmaDetailStore.useDispatch()
  const [csvImportOpen, setCsvImportOpen] = useState(false)

  const refreshMembers = useCallback(() => {
    if (turma) dispatch(TurmaDetailStore.thunks.fetchTurma(turma.id))
    setCsvImportOpen(false)
  }, [turma, dispatch])

  const columns: ColumnsType<MatriculaModel> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: 'Nome do Aluno',
      key: 'nome',
      render: (_, matricula) => <strong>{matricula?.aluno?.nome}</strong>,
    },
    {
      title: 'CPF',
      key: 'cpf',
      width: 180,
      render: (_, matricula) => (
        <Space>
          {matricula?.aluno?.cpf}
          <CopyButton value={matricula?.aluno?.cpf} size="small" />
        </Space>
      ),
    },
    { title: 'Curso', dataIndex: ['curso', 'nomeReduzido'], width: 200 },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (_, matricula) => {
        const statusColors = {
          AGUARDANDO_MATRICULA: '#fa8c16',
          FINALIZADO: 'green',
          MATRICULADO: 'geekblue',
          AGUARDANDO_ENVIO: '#722ed1',
          AGUARDANDO_MATRICULA_MODULO_PRATICO: '#3a3f10',
          AGUARDANDO_CONCLUSAO_MODULO_PRATICO: '#b700ff',
        }
        const color = statusColors[matricula?.statusDetran] || '#444'
        const text = matricula?.statusDetran?.replace(/_/g, ' ') || 'Sem status'
        return (
          <Tag color={color} style={{ textTransform: 'capitalize' }}>
            {text.toLowerCase()}
          </Tag>
        )
      },
    },
    { title: 'UF', dataIndex: 'uf', width: 60 },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, matricula) => (
        <ViewMatriculaButton
          onClick={() => {
            const url = `/cursos/${matricula?.curso?.id}/usuarios/${matricula?.aluno?.id}/acompanhamento?matriculaId=${matricula.id}`
            window.open(url, '_blank')
          }}
        >
          Ver
        </ViewMatriculaButton>
      ),
    },
  ]

  return (
    <SegmentContainer>
      <div style={{ marginBottom: 16 }}>
        <Button type="default" onClick={() => setCsvImportOpen(true)}>
          Importar CSV
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={matriculas}
        rowKey="id"
        pagination={{ pageSize: 10, showTotal: (total) => `Total de ${total} alunos` }}
      />

      <Modal
        title="Importar alunos via CSV"
        open={csvImportOpen}
        onCancel={() => setCsvImportOpen(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        {turma && <ImportarCsvTurma turmaId={turma.id} onImportComplete={refreshMembers} />}
      </Modal>
    </SegmentContainer>
  )
}

const Layout = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
`

const Title = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const ContentWrapper = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 20px;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: #ffffff;
  border-radius: 10px;
`

const SegmentContainer = styled.div`
  padding: 20px;
`

const SubTitle = styled.h3`
  margin: 24px 0 16px 0;
  color: #494d50;
  font-size: 18px;
  font-weight: 500;
`

const InfoText = styled.p`
  margin-top: 16px;
  color: #666;
  font-style: italic;
`

const ViewMatriculaButton = styled.button`
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    filter: brightness(0.9);
  }
`
